/**
 * 🎨 The Architect - Dark + Purple Shine Theme
 * Centralized theme configuration for consistent styling across the app
 */

// Color Palette
export const colors = {
  // Primary Backgrounds - Keep black background, but update components
  primary: '#0A0A0A',        // Main background (keep original black)
  secondary: '#2A2D36',      // Input fields, cards 
  tertiary: '#343842',       // Hover states, lighter backgrounds

  // Purple Shine Accents
  accent: '#7B61FF',         // Primary purple
  accentHover: '#8A6DFF',    // Purple hover state
  accentLight: '#BA87FF',    // Light purple for gradients

  // Text Colors
  textPrimary: '#FFFFFF',    // Main text
  textSecondary: '#CCCCCC',  // Descriptions, helper text
  textMuted: '#8B8E98',      // Muted text (blue-gray tint)

  // Borders & Dividers
  border: '#3A3D46',         // Input borders, section dividers (lighter gray)
  borderLight: '#4A4D56',    // Lighter borders for hover states

  // Special Colors
  gold: '#FFD700',           // Success/highlight (icons, callouts)
  success: '#10B981',        // Success states
  warning: '#F59E0B',        // Warning states
  error: '#EF4444',          // Error states

  // Status Colors (for dark theme)
  successDark: '#10B981',
  warningDark: '#F59E0B',
  errorDark: '#F87171',
};

// Shadow/Glow Effects
export const shadows = {
  // Purple glows
  purpleGlow: '0px 0px 12px rgba(123, 97, 255, 0.3)',
  purpleGlowHover: '0px 0px 16px rgba(123, 97, 255, 0.5)',
  purpleGlowSubtle: '0px 0px 8px rgba(123, 97, 255, 0.3)',

  // Card shadows
  card: '0 0 20px rgba(123, 97, 255, 0.05)',
  cardHover: '0 0 20px rgba(123, 97, 255, 0.1)',

  // Modal shadows
  modal: '0 0 20px rgba(123, 97, 255, 0.2)',
};

// Gradients
export const gradients = {
  purple: 'linear-gradient(to right, #7B61FF, #BA87FF)',
  purpleSubtle: 'linear-gradient(to right, rgba(123, 97, 255, 0.1), rgba(186, 135, 255, 0.1))',
  purpleBorder: 'linear-gradient(to right, rgba(123, 97, 255, 0.3), rgba(186, 135, 255, 0.3))',
};

// Typography
export const typography = {
  // Font families
  fontFamily: {
    primary: 'Inter, system-ui, -apple-system, sans-serif',
    mono: 'JetBrains Mono, Monaco, monospace',
  },

  // Font sizes
  fontSize: {
    xs: '0.75rem',     // 12px
    sm: '0.875rem',    // 14px
    base: '1rem',      // 16px
    lg: '1.125rem',    // 18px
    xl: '1.25rem',     // 20px
    '2xl': '1.5rem',   // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem',  // 36px
    '5xl': '3rem',     // 48px
  },

  // Font weights
  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },
};

// Spacing
export const spacing = {
  // Standard spacing scale
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  8: '2rem',      // 32px
  10: '2.5rem',   // 40px
  12: '3rem',     // 48px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
  24: '6rem',     // 96px
};

// Border Radius
export const borderRadius = {
  sm: '0.375rem',   // 6px
  md: '0.5rem',     // 8px
  lg: '0.75rem',    // 12px
  xl: '1rem',       // 16px
  '2xl': '1.5rem',  // 24px
  full: '9999px',
};

// Animation/Transitions
export const transitions = {
  fast: 'all 0.15s ease-in-out',
  normal: 'all 0.2s ease-in-out',
  slow: 'all 0.3s ease-in-out',
};

// Component Styles (Tailwind class combinations)
export const components = {
  // Buttons - Updated to match the sleek white button style
  button: {
    base: 'font-medium rounded-xl transition-all duration-200 disabled:cursor-not-allowed',
    primary: 'bg-[#7B61FF] hover:bg-[#6A51E6] text-white shadow-lg hover:shadow-xl shadow-[#7B61FF]/20',
    secondary: 'backdrop-blur-md bg-white/5 text-white hover:bg-white/10 border border-white/10',
    danger: 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30',
    ghost: 'text-[#CCCCCC] hover:text-white hover:bg-[#2A2D36]',
    disabled: 'bg-[#3A3A3A] text-[#CCCCCC] cursor-not-allowed border border-[#4A4A4A]',
  },

  // Cards
  card: {
    base: 'backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl transition-all duration-200',
    hover: 'hover:bg-white/10 hover:border-white/20',
    shadow: 'shadow-[0_0_20px_rgba(123,97,255,0.1)]',
    shadowHover: 'hover:shadow-[0_0_20px_rgba(123,97,255,0.1)]',
  },

  // Inputs - Updated to match the dark input style from the images
  input: {
    base: 'w-full bg-[#2A2D36] border border-[#3A3D46] text-white rounded-xl px-4 py-3 transition-all duration-200 placeholder-[#8B8E98]',
    focus: 'focus:border-[#7B61FF] focus:ring-1 focus:ring-[#7B61FF] focus:bg-[#343842]',
    error: 'border-red-400 focus:border-red-400 focus:ring-red-400',
  },

  // Text
  text: {
    primary: 'text-white',
    secondary: 'text-[#CCCCCC]',
    muted: 'text-[#8B8E98]',
    accent: 'text-[#7B61FF]',
    gold: 'text-[#FFD700]',
    success: 'text-green-400',
    warning: 'text-yellow-400',
    error: 'text-red-400',
  },

  // Backgrounds - Keep black main background
  background: {
    primary: 'bg-[#0A0A0A]',     // Keep original black background
    secondary: 'bg-[#2A2D36]',   // Input/card backgrounds 
    tertiary: 'bg-[#343842]',    // Hover states
    accent: 'bg-[#7B61FF]',
    accentSubtle: 'bg-[#7B61FF]/10',
    gradient: 'bg-gradient-to-r from-[#7B61FF]/10 to-[#BA87FF]/10',
  },

  // Borders
  border: {
    base: 'border-[#3A3D46]',    // Lighter border like in the images
    accent: 'border-[#7B61FF]',
    accentSubtle: 'border-[#7B61FF]/30',
    light: 'border-[#4A4D56]',
  },

  // Modals/Overlays
  modal: {
    backdrop: 'fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50',
    container: 'backdrop-blur-xl bg-black/80 border border-white/10 rounded-2xl shadow-2xl',
  },

  // Navigation
  nav: {
    active: 'text-[#7B61FF] font-medium',
    inactive: 'text-[#CCCCCC] hover:text-white transition-colors',
  },

  // Status indicators
  status: {
    success: 'text-green-400 bg-green-400/20',
    warning: 'text-[#FFD700] bg-[#FFD700]/20',
    error: 'text-red-400 bg-red-400/20',
    info: 'text-[#7B61FF] bg-[#7B61FF]/20',
    secondary: 'text-[#CCCCCC] bg-[#2A2D36]',
  },
};

// Layout
export const layout = {
  maxWidth: {
    sm: 'max-w-sm',      // 384px
    md: 'max-w-md',      // 448px
    lg: 'max-w-lg',      // 512px
    xl: 'max-w-xl',      // 576px
    '2xl': 'max-w-2xl',  // 672px
    '4xl': 'max-w-4xl',  // 896px
    '6xl': 'max-w-6xl',  // 1152px
    '7xl': 'max-w-7xl',  // 1280px
  },

  container: 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8',

  // Professional grid systems for consistent alignment
  grid: {
    // Standard grids
    cols2: 'grid grid-cols-1 md:grid-cols-2 gap-6',
    cols3: 'grid grid-cols-1 md:grid-cols-3 gap-6',
    cols4: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6',

    // Metric card grids (like Credit Balance section)
    metrics2: 'grid grid-cols-1 md:grid-cols-2 gap-4',
    metrics3: 'grid grid-cols-1 md:grid-cols-3 gap-4',
    metrics4: 'grid grid-cols-2 lg:grid-cols-4 gap-4',

    // Responsive metric grids
    metricsResponsive: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4',
  }
};

// Professional design presets for consistency across all pages
export const presets = {
  // Metric cards for dashboard-style displays
  metricCard: {
    container: 'bg-[#2A2D36] border border-[#3A3D46] rounded-lg p-4',
    value: 'text-2xl font-bold text-white',
    label: 'text-sm text-[#CCCCCC]',
    accent: 'text-[#7B61FF]',
    success: 'text-green-400',
    warning: 'text-orange-400',
    error: 'text-red-400',
  },

  // Internal sections within cards (input color backgrounds)
  internal: {
    section: 'bg-[#2A2D36] border border-[#3A3D46] rounded-lg p-4',
    divider: 'border-[#3A3D46]',
    nested: 'bg-[#1A1A1A] border border-[#3A3D46] rounded-lg p-3',
    highlight: 'bg-[#2A2D36] border border-[#7B61FF]/30 rounded-lg p-4',
  },

  // Professional spacing patterns
  spacing: {
    section: 'space-y-6',
    cards: 'space-y-4',
    tight: 'space-y-2',
    metrics: 'space-y-4',
    content: 'space-y-3',
  },

  // Feature grid layouts
  features: {
    grid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
    card: 'bg-[#1A1A1A] border border-[#3A3D46] rounded-lg p-6',
  },

  // Plan/pricing layouts
  plans: {
    grid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
    card: 'bg-[#1A1A1A] border border-[#3A3D46] rounded-xl p-6',
    popular: 'bg-[#1A1A1A] border-2 border-[#7B61FF] rounded-xl p-6 relative',
  }
};

// Logo Configuration
export const logo = {
  // Logo paths
  paths: {
    main: '/architect_logo.png',           // Main logo (used in LogoText: "The [LOGO]rchitect")
    black: '/the architect logo.png',      // Black version for light backgrounds
    blackBg: '/logoblackbackground.png',   // Logo with black background
  },

  // Logo sizes for different use cases
  sizes: {
    xs: 'h-6 w-auto',      // 24px - Small icons, mobile menu
    sm: 'h-8 w-auto',      // 32px - Navigation, compact headers
    md: 'h-10 w-auto',     // 40px - Default header size
    lg: 'h-12 w-auto',     // 48px - Page headers, hero sections
    xl: 'h-16 w-auto',     // 64px - Large displays, branding
    '2xl': 'h-20 w-auto',  // 80px - Hero sections, landing
  },

  // Logo alt text
  alt: 'The Architect - Transform Ideas into AI-Ready Prompts',
}

// Export complete theme object
export const theme = {
  colors,
  shadows,
  gradients,
  typography,
  spacing,
  borderRadius,
  transitions,
  components,
  layout,
  presets,
  logo,
};

export default theme;